import { useEffect, useState } from "react";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import { ApiRequest } from "GlobalFunctions";
import { formatCurrency } from "../helpers/invoiceUtils";

interface LineItem {
  description: string;
  quantity: number;
  price: number;
  tax_percent: number;
  unit_code: string;
  product_code: string;
}

interface CreditNoteDialogProps {
  open: boolean;
  onClose: () => void;
  doc: any;
  onSuccess: () => void;
}

export default function CreditNoteDialog({ open, onClose, doc, onSuccess }: CreditNoteDialogProps) {
  const [step, setStep] = useState<"type" | "items">("type");
  const [ncType, setNcType] = useState<"total" | "partial">("total");
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [availableQty, setAvailableQty] = useState<number[]>([]);
  const [selected, setSelected] = useState<boolean[]>([]);
  const [quantities, setQuantities] = useState<number[]>([]);
  const [hasPreviousNC, setHasPreviousNC] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("type");
      setNcType("total");
      setLineItems([]);
      setHasPreviousNC(false);
      // Check if there are previous credit notes
      ApiRequest({
        url: "/dian/invoice/detail",
        method: "get",
        headers: { ltkn: localStorage.getItem("ltkn") },
        query: { document_id: doc.document_id },
        setResponse: (response: any) => {
          const hasNC = (response.document?.creditNotes?.length || 0) > 0;
          setHasPreviousNC(hasNC);
          if (hasNC) setNcType("partial");
        },
      });
    }
  }, [open]);

  const loadItems = () => {
    setLoadingItems(true);
    ApiRequest({
      url: "/dian/invoice/detail",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query: { document_id: doc.document_id },
      setResponse: (response: any) => {
        const items: LineItem[] = response.document?.lineItems || [];
        const credited: number[] = response.document?.creditedQuantities || items.map(() => 0);
        const available = items.map((item, i) => Math.max(0, item.quantity - (credited[i] || 0)));
        setLineItems(items);
        setAvailableQty(available);
        setSelected(available.map(q => q > 0));
        setQuantities([...available]);
        setLoadingItems(false);
      },
    });
  };

  const handleNext = () => {
    if (ncType === "total") {
      handleSubmitTotal();
    } else {
      loadItems();
      setStep("items");
    }
  };

  const handleSubmitTotal = () => {
    setLoading(true);
    ApiRequest({
      url: "/dian/invoice/void",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: { document_id: doc.document_id },
      setResponse: (response: any) => {
        setLoading(false);
        if (response.success) {
          onSuccess();
          onClose();
        }
      },
    });
  };

  const handleSubmitPartial = () => {
    const items = lineItems
      .map((item, i) => selected[i] && quantities[i] > 0 ? { ...item, quantity: quantities[i] } : null)
      .filter(Boolean);

    if (!items.length) return;
    setLoading(true);

    ApiRequest({
      url: "/dian/invoice/partial-void",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: {
        document_id: doc.document_id,
        lineItems: items,
        discrepancyCode: "1",
        discrepancyDescription: "Devolución parcial de productos",
      },
      setResponse: (response: any) => {
        setLoading(false);
        if (response.success) {
          onSuccess();
          onClose();
        }
      },
    });
  };

  const selectedTotal = lineItems.reduce((sum, item, i) => {
    if (!selected[i] || quantities[i] <= 0) return sum;
    const subtotal = quantities[i] * item.price;
    return sum + subtotal + subtotal * (item.tax_percent / 100);
  }, 0);

  const allFullyCredited = availableQty.every(q => q === 0);

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Nota Crédito — {doc?.prefix}{doc?.number}
      </DialogTitle>

      <DialogContent>
        {step === "type" && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Seleccione el tipo de Nota Crédito a emitir:
            </Typography>
            <RadioGroup value={ncType} onChange={(e) => setNcType(e.target.value as any)}>
              <FormControlLabel value="total" control={<Radio />} disabled={hasPreviousNC}
                label={<Box><Typography fontWeight={500} color={hasPreviousNC ? "text.disabled" : undefined}>Anulación Total</Typography><Typography variant="caption" color="text.secondary">{hasPreviousNC ? "No disponible: ya existen NC parciales sobre esta factura" : "Se anula la factura completa. Código DIAN: 2 (Revocación)"}</Typography></Box>}
              />
              <FormControlLabel value="partial" control={<Radio />} sx={{ mt: 1 }}
                label={<Box><Typography fontWeight={500}>Devolución Parcial</Typography><Typography variant="caption" color="text.secondary">Retirar productos o reducir cantidades. Código DIAN: 1</Typography></Box>}
              />
            </RadioGroup>
          </Box>
        )}

        {step === "items" && (
          <Box sx={{ py: 1 }}>
            {loadingItems ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
            ) : allFullyCredited ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                Todos los productos de esta factura ya han sido devueltos en su totalidad.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Seleccione los productos y cantidades a devolver:
                </Typography>

                <TableContainer sx={{ maxHeight: 350 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Producto</TableCell>
                        <TableCell align="right">Disponible</TableCell>
                        <TableCell align="right">Cant. a devolver</TableCell>
                        <TableCell align="right">Precio</TableCell>
                        <TableCell align="right">Subtotal NC</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.map((item, i) => (
                        <TableRow key={i} sx={{ opacity: availableQty[i] === 0 ? 0.4 : selected[i] ? 1 : 0.6 }}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={selected[i]} disabled={availableQty[i] === 0}
                              onChange={(e) => { const s = [...selected]; s[i] = e.target.checked; setSelected(s); }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{item.description}</Typography>
                            {availableQty[i] < item.quantity && (
                              <Typography variant="caption" color="warning.main">
                                (Original: {item.quantity}, ya devuelto: {item.quantity - availableQty[i]})
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{availableQty[i]}</TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number" size="small" variant="outlined"
                              value={quantities[i]}
                              disabled={!selected[i] || availableQty[i] === 0}
                              inputProps={{ min: 0, max: availableQty[i], step: 1 }}
                              onChange={(e) => {
                                const q = [...quantities];
                                q[i] = Math.min(Math.max(0, Number(e.target.value)), availableQty[i]);
                                setQuantities(q);
                              }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {selected[i] && quantities[i] > 0
                              ? formatCurrency(quantities[i] * item.price * (1 + item.tax_percent / 100))
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2, alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary">Total Nota Crédito:</Typography>
                  <Chip label={formatCurrency(selectedTotal)} color="error" sx={{ fontWeight: 600, fontSize: 14 }} />
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {step === "items" && (
          <Button onClick={() => setStep("type")} disabled={loading} sx={{ mr: "auto", borderRadius: 2, textTransform: "none" }}>
            Atrás
          </Button>
        )}
        <Button onClick={onClose} disabled={loading} sx={{ borderRadius: 2, textTransform: "none" }}>Cancelar</Button>
        {step === "type" && (
          <Button variant="contained" color={ncType === "total" ? "error" : "primary"} onClick={handleNext} disabled={loading} sx={{ borderRadius: 2, textTransform: "none" }}>
            {loading ? <CircularProgress size={20} /> : ncType === "total" ? "Anular Factura" : "Siguiente"}
          </Button>
        )}
        {step === "items" && !allFullyCredited && (
          <Button variant="contained" color="error" onClick={handleSubmitPartial}
            disabled={loading || !lineItems.some((_, i) => selected[i] && quantities[i] > 0)}
            sx={{ borderRadius: 2, textTransform: "none" }}>
            {loading ? <CircularProgress size={20} /> : "Emitir Nota Crédito"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
